// public/js/core/coreRouter.js - Fixed version to prevent duplicate content
import navigationController from '@modules/navigation/navigationController.js';
import contactFormController from '@modules/services/contactFormController.js';
// DON'T import coreUiComponents here - it will be lazy loaded when needed
import auth from '@modules/auth/authController.js';
import api from '@modules/services/api.js';
import { createLogger } from '@utils/logger.js';
import settingAccessControl from '@modules/setting/settingAccessControl.js';
import appNavHtml from '@views/app_nav.html?raw';

class CoreRouter {
  constructor() {
    if (CoreRouter.instance) return CoreRouter.instance;
    CoreRouter.instance = this;

    this.logger = createLogger('Router');

    // Listen for hash changes (handles ALL navigation: links, back/forward, programmatic)
    window.addEventListener("hashchange", (e) => this._handleHashChange(e));
    document.addEventListener("click", (e) => this._handleClick(e));

    // Variables
    this.currentPath = null;
    this.currentNavigationContext = null;
    this.navigationController = navigationController;
    this.contactFormController = contactFormController;
    this.coreUiComponents = null; // Will be lazy-loaded when authenticated user needs it
    this.auth = auth;
    this.api = api;

    // ADD: Prevent duplicate route handling
    this.isRouteProcessing = false;
    this.lastProcessedRoute = null;
    this.routeProcessingTimeout = null;
  }

  /**
   * Lazy load coreUiComponents only when needed (for authenticated users)
   */
  async _ensureCoreUiComponents() {
    if (!this.coreUiComponents) {
      this.logger.debug('Lazy loading coreUiComponents...');
      const { default: coreUiComponents } = await import('./coreUiComponents.js');
      this.coreUiComponents = coreUiComponents;
      this.logger.debug('coreUiComponents loaded');
    }
    return this.coreUiComponents;
  }

  async init() {
    if (window.location.pathname.startsWith('/api/')) {
      return;
    }

    await this._handleRoute(window.location.hash);
    this.currentPath = window.location.hash;
  }

  _handleHashChange(e) {
    // Prevent navigation when overlay is open
    if (document.getElementById("overlay")) {
      // Restore previous hash
      if (this.currentPath) {
        window.location.hash = this.currentPath.replace(/^#/, '');
      }
      return;
    }

    // Extract hash from event.newURL (more reliable than window.location.hash)
    let newHash = '';
    if (e && e.newURL) {
      const url = new URL(e.newURL);
      newHash = url.hash;
    } else {
      newHash = window.location.hash;
    }

    // Process the route change
    this._debouncedHandleRoute(newHash);
    this.currentPath = newHash;
  }

  // SOLUTION 1: Modify your router's _handleClick method
  _handleClick(e) {
      const tagName = e.target.tagName;
      let path = null;
    
      if (tagName === "A" || e.target.closest("A")) {
        const anchor = e.target.closest("A");
        path = anchor.getAttribute("href");
        
        if (anchor.hasAttribute('download')) {
          this.logger.debug('Ignoring download link');
          return;
        }

        if (path && path.startsWith('blob:')) {
          this.logger.debug('Ignoring blob URL');
          return;
        }
    
        if (path && (path.startsWith('http://') || path.startsWith('https://'))) {
          window.open(path, "_blank");
          e.preventDefault();
          return; 
        }

        e.preventDefault();
      }
    
      if (path) {
        if (path.startsWith('/#/')) {
          path = path.substring(1);
        } else if (!path.startsWith('#/') && !path.startsWith('#')) {
          path = `#/${path.replace(/^\//, '')}`;
        }
        
        window.location.hash = path.replace(/^#/, '');
        this.currentPath = path;
      }
  }

  // ADDED: Debounced route handling to prevent duplicates
  _debouncedHandleRoute(route) {
    // Clear any existing timeout
    if (this.routeProcessingTimeout) {
      clearTimeout(this.routeProcessingTimeout);
    }

    // If the same route is already being processed, ignore
    if (this.isRouteProcessing && this.lastProcessedRoute === route) {
      return;
    }

    // Set a small timeout to debounce rapid fire events
    this.routeProcessingTimeout = setTimeout(() => {
      this._handleRoute(route);
    }, 10);
  }

  navigateTo(route) {
    if (route.startsWith('/shared/')) {
      window.location.href = route;
    } else {
      if (route.startsWith('/#/')) {
        route = route.substring(1);
      } else if (!route.startsWith('#/') && !route.startsWith('#')) {
        route = `#/${route.replace(/^\//, '')}`;
      }
      
      window.location.hash = route.replace(/^#/, '');
    }
  }

  _normalizeRoute(route) {
    if (!route || route === "" || route === "#" || route === "#/" || route === "/") {
      return "root";
    }
    
    return route.replace(/^#?\/?/, '');
  }

  _extractRouteParams(route, pattern) {
    const routeParts = route.split('/');
    const patternParts = pattern.split('/');
    
    if (routeParts.length !== patternParts.length) {
      return null;
    }
    
    const params = {};
    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i] === '*') {
        params.id = routeParts[i];
      } else if (patternParts[i] !== routeParts[i]) {
        return null;
      }
    }
    
    return params;
  }

  _canUpdateNavigation() {
    return this.navigationController && 
           this.navigationController.modeController &&
           this.navigationController.initialized;
  }

  _updateNavigation(mode, delay = 0) {
    if (!this._canUpdateNavigation()) return;
    
    if (delay > 0) {
      setTimeout(() => {
        this.navigationController.modeController.toggleButtons(mode);
      }, delay);
    } else {
      this.navigationController.modeController.toggleButtons(mode);
    }
  }

  _getNavigationContext(normalizedRoute) {
    if (normalizedRoute === "root" || normalizedRoute.includes("sites/")) {
      return "site";
    } else if (normalizedRoute.startsWith("dashboard")) {
      return "dashboard";
    } else if (normalizedRoute.startsWith("shared/")) {
      return "shared";
    }
    return "site";
  }

  async _loadSiteControls() {
    const nav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");

    if (nav) {
      this.logger.debug('Found nav element:', nav);
      // Load app_nav HTML from imported template
      nav.innerHTML = appNavHtml;
      nav.id = "app-nav";
      this.logger.debug('Loaded site navigation into', nav.id);
    } else {
      this.logger.warn('Navigation element not found in _loadSiteControls');
    }

    // Load footer content
    const footerContainer = document.getElementById("footer-content");
    if (footerContainer) {
      const footerContent = `
        <div class="footer-gallery">
          <div class="footer-gallery-elements">
            <a class="nav-element footerMail footer-elements" id="contact" href="/sites/contact">
              <span class="material-symbols-outlined">mail</span> Kontakt
            </a>
            <a class="nav-element footerPrivacy footer-elements" id="privacy" href="/sites/privacy">
              <span class="material-symbols-outlined">lock</span> Datenschutz
            </a>
          </div>
        </div>
      `;
      footerContainer.innerHTML = footerContent;
    }

    const breadCrumbs = document.getElementById("bread-crumbs");
    if (breadCrumbs) {
      breadCrumbs.innerHTML = "";
    }

    if (auth && this.auth.updateLoginControls) {
      this.auth.updateLoginControls();
    }
  }

  async _loadDashboardControls(){
    if (await this.auth.isAuthenticated()) {
      const nav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");
      const footer = document.getElementById("footer-content");

      if (footer){
        footer.remove();
      }

      if(nav){
        this.logger.debug('Found nav element:', nav);
        // Load app_nav HTML from imported template
        nav.innerHTML = appNavHtml;
        nav.id = "app-nav";
        this.logger.debug('Loaded dashboard navigation into', nav.id);
      } else {
        this.logger.warn('Navigation element not found in _loadDashboardControls');
      }

      this.auth.updateLoginControls();
    } else {
      const contentContainer = document.getElementById("content-container");
      contentContainer.innerHTML = `<div><h1>Bitte melden Sie sich an, um Ihre Inhalte anzuzeigen.</h1></div>`;
    }
  }

  async _updateBreadcrumbs(normalizedRoute) {
    if (await this.auth.isAuthenticated()) {
      const bread_crumbs = document.getElementById("bread-crumbs");
      if (bread_crumbs) {
        const breadcrumbHtml = await this._generateBreadcrumbs(normalizedRoute);
        bread_crumbs.innerHTML = breadcrumbHtml;
      }
    }
  }

  async _generateBreadcrumbs(normalizedRoute) {
    if (!normalizedRoute || !normalizedRoute.startsWith("dashboard")) {
      return "";
    }

    const PANELS = ['dogs', 'results', 'activities', 'notes', 'files', 'trash', 'messages', 'parcours', 'settings'];
    
    // FIXED: Use direct controller references instead of this.api
    const IDENTIFIER_CONFIG = {
      'dog': {
        async fetchDisplayName(id) {
          try {
            const dog = await this.api.apiCall(`/api/dogs/${id}`, "GET");
            return dog ? `Dog: ${dog.data.nick_name}` : `Dog: ${id}`;
          } catch (error) {
            this.logger.error('Error fetching dog data:', error);
            return `Dog: ${id}`;
          }
        }
      },
      'activity': {
        async fetchDisplayName(id) {
          try {
            const activity  = await this.api.apiCall(`/api/activities/${id}`, "GET");
            return activity ? `Activity: ${activity.data.title}` : `Activity: ${id}`;
          } catch (error) {
            // Silently handle 404 errors (deleted activities)
            if (error.status === 404) {
              return null; // Signal to skip this breadcrumb
            }
            this.logger.error('Error fetching activity data:', error);
            return `Activity: ${id}`;
          }
        }
      },
      'parcours': {
        async fetchDisplayName(id) {
          try {
            const parcour = await this.api.apiCall(`/api/parcours/${id}`, "GET");
            return parcour ? `Parcour: ${parcour.data.title}` : `Parcour: ${id}`;
          } catch (error) {
            this.logger.error('Error fetching parcour data:', error);
            return `Parcour: ${id}`;
          }
        }
      }
    };

    
    
    const parts = normalizedRoute.split('/');
    let breadcrumbs = [];
    let currentPath = "";

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      
      if (i === 0 && part === "dashboard") {
        currentPath = "dashboard";
        breadcrumbs.push(`<a href="#/${currentPath}" class="breadcrumb-link">Dashboard</a>`);
        
      } else if (i === 1 && PANELS.includes(part)) {
        currentPath += `/${part}`;
        const displayName = part.charAt(0).toUpperCase() + part.slice(1); 
        breadcrumbs.push(`<a href="#/${currentPath}" class="breadcrumb-link">${displayName}</a>`);
        
      } else if (IDENTIFIER_CONFIG[part]) {
        const identifier = part;
        const id = parts[i + 1];

        if (id) {
          currentPath += `/${identifier}/${id}`;

          // FIXED: Bind the context properly
          const displayName = await IDENTIFIER_CONFIG[identifier].fetchDisplayName.call(this, id);

          // Skip breadcrumb if displayName is null (entity was deleted)
          if (displayName !== null) {
            breadcrumbs.push(`<a href="#/${currentPath}" class="breadcrumb-link">${displayName}</a>`);
          }
          i++;
        }
      }
    }

    return breadcrumbs.join(' <span class="breadcrumb-separator">/</span> ');
  }
  
  async _handleRoute(route) {
    // ADDED: Prevent multiple simultaneous route processing
    if (this.isRouteProcessing && this.lastProcessedRoute === route) {
      return;
    }

    this.isRouteProcessing = true;
    this.lastProcessedRoute = route;

    try {
      const contentContainer = document.getElementById("content-container");
      if (!contentContainer) return;

      const normalizedRoute = this._normalizeRoute(route);
      const requiredContext = this._getNavigationContext(normalizedRoute);

      // Handle root route: redirect based on authentication
      if (normalizedRoute === "root") {
        const isAuthenticated = await this.auth.isAuthenticated();
        if (isAuthenticated) {
          // Authenticated user: redirect to dashboard
          window.location.hash = '#/dashboard';
        } else {
          // Unauthenticated user: redirect to login
          window.location.href = '/login.html';
        }
        return;
      }

      // CHECK: Module access control for dashboard routes
      if (requiredContext === "dashboard" && await this.auth.isAuthenticated()) {
        const isAccessible = await settingAccessControl.isRouteAccessible(normalizedRoute);
        if (!isAccessible) {
          // Redirect to dashboard if module is disabled
          this.logger.info({ route: normalizedRoute }, 'Route blocked - module disabled');
          window.location.hash = '#/dashboard';
          return;
        }
      }

      if (this.currentNavigationContext !== requiredContext) {
        if (requiredContext === "site") {
          await this._loadSiteControls();
        } else if (requiredContext === "dashboard") {
          await this._loadDashboardControls();
        }

        this.currentNavigationContext = requiredContext;
      }

      // OPTIMIZED: Consolidated route handling
      // IMPORTANT: Load route content FIRST, then update breadcrumbs
      switch (true) {
        case normalizedRoute === "root":
          await this._handleRootRoute();
          break;

        case normalizedRoute === "login":
          await this._handleLoginRoute();
          break;

        case normalizedRoute === "register":
          await this._handleRegisterRoute();
          break;

        case normalizedRoute === "reset-password":
          await this._handleResetPasswordRoute();
          break;

        case normalizedRoute === "dashboard":
          await this._handleAuthenticatedRoute('dashboard');
          break;

        case normalizedRoute === "dashboard/users":
          await this._handleAuthenticatedRoute('users_dashboard');
          break;

        case normalizedRoute.startsWith("dashboard/users/user"):
          const userParams = this._extractRouteParams(normalizedRoute, "dashboard/users/user/*");
          if (userParams) {
            await this._handleAuthenticatedRoute('user_details', { user_id: userParams.id });
          } else {
            await this._handleNotFoundRoute();
          }
          break;

        case normalizedRoute === "dashboard/breeds":
          await this._handleAuthenticatedRoute('breeds_dashboard');
          break;

        case normalizedRoute === "dashboard/manual":
          await this._handleAuthenticatedRoute('manual_dashboard');
          break;

        case normalizedRoute === "dashboard/settings":
          await this._handleAuthenticatedRoute('settings_dashboard');
          break;

        case normalizedRoute === "dashboard/database":
          await this._handleAuthenticatedRoute('database_viewer');
          break;

        case normalizedRoute === "dashboard/support":
          await this._handleAuthenticatedRoute('support_dashboard');
          break;

        case normalizedRoute === "dashboard/monitoring":
          await this._handleAuthenticatedRoute('monitoring_dashboard');
          break;

        case normalizedRoute === "dashboard/backup":
          await this._handleAuthenticatedRoute('backup_dashboard');
          break;

        case normalizedRoute === "dashboard/telemetry":
          await this._handleAuthenticatedRoute('telemetry_dashboard');
          break;

        case normalizedRoute.includes("sites/"):
          const page = normalizedRoute.split("sites/")[1];
          await this._handleSiteRoute(page);
          break;

        case normalizedRoute.startsWith("shared/"):
          const sharedParams = this._extractRouteParams(normalizedRoute, "shared/*");
          if (sharedParams) {
            await this._handleSharedRoute(sharedParams.id);
          } else {
            await this._handleNotFoundRoute();
          }
          break;

        default:
          await this._handleNotFoundRoute();
          break;
      }

      // FIXED: Update breadcrumbs AFTER route content is loaded
      // This prevents trying to fetch data for deleted entities
      if (requiredContext === "dashboard") {
        await this._updateBreadcrumbs(normalizedRoute);
      }

      window.scrollTo({ top: 0, behavior: "smooth" });

    } catch (error) {
      this.logger.error("Route handling error:", error);
      await this._handleNotFoundRoute();
    } finally {
      // ADDED: Always reset processing flag
      setTimeout(() => {
        this.isRouteProcessing = false;
      }, 100);
    }
  }

  // OPTIMIZED: Consolidated authenticated route handler
  async _handleAuthenticatedRoute(uiType, params = {}, navigationMode = null) {
    // Show navigation for authenticated pages
    const appNav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");
    if (appNav) {
      appNav.style.display = '';
    }

    const contentContainer = document.getElementById("content-container");

    if (await this.auth.isAuthenticated()) {
      try {
        // Lazy load coreUiComponents only when authenticated user needs it
        const coreUiComponents = await this._ensureCoreUiComponents();
        await coreUiComponents.loadUI(uiType, params);
        if (navigationMode) {
          this._updateNavigation(navigationMode);
        }
      } catch (error) {
        this.logger.error(`Error loading UI (${uiType}):`, error);
        // Show error message but keep page functional
        contentContainer.innerHTML = `
          <div class="error-container" style="padding: 20px; text-align: center;">
            <h2>Fehler beim Laden der Daten</h2>
            <p>Die Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.</p>
            <p style="color: #666; font-size: 0.9em;">${error.message || 'Unbekannter Fehler'}</p>
          </div>
        `;
      }
    } else {
      // Redirect unauthenticated users to login page
      window.location.href = '/login.html';
    }
  }

  _showWelcomePage() {
    const contentContainer = document.getElementById("content-container");
    contentContainer.innerHTML = `
      <div style="max-width: 800px; margin: 50px auto; padding: 40px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <h1 style="font-size: 2.5em; margin-bottom: 20px; color: #333;">Willkommen bei CaninCloud</h1>
        <p style="font-size: 1.2em; color: #666; margin-bottom: 40px;">
          Verwalten Sie Ihre Agility-Hunde, Aktivitäten und Ergebnisse an einem Ort.
        </p>

        <div style="background: #f5f5f5; padding: 30px; border-radius: 10px; margin-bottom: 30px;">
          <h2 style="color: #555; margin-bottom: 15px;">Funktionen</h2>
          <ul style="list-style: none; padding: 0; text-align: left; max-width: 500px; margin: 0 auto;">
            <li style="padding: 10px; margin: 5px 0;">✓ Hundedaten verwalten</li>
            <li style="padding: 10px; margin: 5px 0;">✓ Aktivitäten tracken</li>
            <li style="padding: 10px; margin: 5px 0;">✓ Parcours erstellen</li>
            <li style="padding: 10px; margin: 5px 0;">✓ Ergebnisse dokumentieren</li>
            <li style="padding: 10px; margin: 5px 0;">✓ Gewichtsverlauf überwachen</li>
          </ul>
        </div>

        <button
          id="welcome-login-btn"
          style="
            background: #4CAF50;
            color: white;
            border: none;
            padding: 15px 40px;
            font-size: 1.1em;
            border-radius: 5px;
            cursor: pointer;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            transition: background 0.3s;
          "
          onmouseover="this.style.background='#45a049'"
          onmouseout="this.style.background='#4CAF50'"
        >
          Jetzt anmelden
        </button>

        <p style="margin-top: 30px; color: #999; font-size: 0.9em;">
          Noch kein Konto? <a href="#" id="welcome-register-link" style="color: #4CAF50;">Registrieren</a>
        </p>
      </div>
    `;

    // Add event listeners
    const loginBtn = document.getElementById('welcome-login-btn');
    const registerLink = document.getElementById('welcome-register-link');

    if (loginBtn) {
      loginBtn.addEventListener('click', () => {
        this.auth.login();
      });
    }

    if (registerLink) {
      registerLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.auth.showRegisterOverlay();
      });
    }
  }

  async _handleRootRoute() {
    const contentContainer = document.getElementById("content-container");
    // Simple landing page content
    const pageContent = `
      <div class="hero-container">
        <div class="hero-content">
          <h1 class="hero-title">canincloud.com</h1>
        </div>
      </div>
    `;
    contentContainer.innerHTML = pageContent;
    this._updateNavigation("root", 50);
  }

  async _handleSiteRoute(page) {
    const contentContainer = document.getElementById("content-container");

    // Static content for site pages
    const pageContentMap = {
      'about': '<h1>About</h1><p>Information about CaninCloud.</p>',
      'contact': '<h1>Contact</h1><p>Get in touch with us.</p>',
      'privacy': '<h1>Privacy</h1><p>Privacy policy information.</p>',
    };

    contentContainer.innerHTML = pageContentMap[page] || `<div><h1>Page not found.</h1></div>`;
    this._updateNavigation("root", 50);
  }

  async _handleSharedRoute(sharedId) {
    // TODO: Implement shared route handling
    this.logger.info("Shared route:", sharedId);
  }

  async _handleNotFoundRoute() {
    const contentContainer = document.getElementById("content-container");
    contentContainer.innerHTML = `<div><h1>Page not found.</h1></div>`;
  }

  /**
   * Handle login route - Full page login form
   */
  async _handleLoginRoute() {
    // Hide navigation for auth pages
    const appNav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");
    if (appNav) {
      appNav.style.display = 'none';
    }

    const contentContainer = document.getElementById("content-container");

    contentContainer.innerHTML = `
      <div class="auth-page">
        <div class="auth-container">
          <div class="auth-header">
            <img src="/images/logo.png" alt="CaninCloud Logo" class="auth-logo">
            <h1>CaninCloud</h1>
            <p>Melden Sie sich an, um fortzufahren</p>
          </div>

          <div id="login-error" class="error-message" style="display: none;"></div>

          <form id="login-form" class="auth-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required autofocus>
            </div>

            <div class="form-group">
              <label for="password">Passwort</label>
              <input type="password" id="password" name="password" required>
            </div>

            <button type="submit" class="btn-primary btn-full-width">Anmelden</button>

            <div class="auth-links">
              <a href="#/reset-password" class="auth-link">Passwort vergessen?</a>
              <span>|</span>
              <a href="#/register" class="auth-link">Registrieren</a>
            </div>
          </form>
        </div>
      </div>
    `;

    // Set up event listener for login form
    const loginForm = document.getElementById('login-form');
    if (loginForm) {
      loginForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this._handleLoginSubmit(e);
      });
    }

    this._updateNavigation("root");
  }

  /**
   * Handle login form submission
   */
  async _handleLoginSubmit(e) {
    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    const errorMessage = document.getElementById('login-error');
    const submitButton = e.target.querySelector('button[type="submit"]');

    try {
      errorMessage.style.display = 'none';
      submitButton.disabled = true;
      submitButton.textContent = 'Anmeldung läuft...';

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Handle successful login
      await this.auth.handleLoginSuccess(data);

    } catch (error) {
      errorMessage.textContent = error.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.';
      errorMessage.style.display = 'block';
      submitButton.disabled = false;
      submitButton.textContent = 'Anmelden';
    }
  }

  /**
   * Handle register route - Full page registration form
   */
  async _handleRegisterRoute() {
    // Hide navigation for auth pages
    const appNav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");
    if (appNav) {
      appNav.style.display = 'none';
    }

    const contentContainer = document.getElementById("content-container");

    contentContainer.innerHTML = `
      <div class="auth-page">
        <div class="auth-container">
          <div class="auth-header">
            <img src="/images/logo.png" alt="CaninCloud Logo" class="auth-logo">
            <h1>CaninCloud</h1>
            <p>Erstellen Sie Ihr Konto</p>
          </div>

          <div id="register-error" class="error-message" style="display: none;"></div>
          <div id="register-success" class="success-message" style="display: none;"></div>

          <form id="register-form" class="auth-form">
            <div class="form-group">
              <label for="firstName">Vorname</label>
              <input type="text" id="firstName" name="firstName" required autofocus>
            </div>

            <div class="form-group">
              <label for="lastName">Nachname</label>
              <input type="text" id="lastName" name="lastName" required>
            </div>

            <div class="form-group">
              <label for="gender">Geschlecht</label>
              <select id="gender" name="gender">
                <option value="prefer_not_to_say">Keine Angabe</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="other">Andere</option>
              </select>
            </div>

            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required>
            </div>

            <div class="form-group">
              <label for="password">Passwort</label>
              <input type="password" id="password" name="password" required minlength="8">
              <div class="form-hint">Mindestens 8 Zeichen, eine Zahl und ein Sonderzeichen</div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Passwort bestätigen</label>
              <input type="password" id="confirmPassword" name="confirmPassword" required minlength="8">
            </div>

            <button type="submit" class="btn-primary btn-full-width">Konto erstellen</button>

            <div class="auth-links">
              <a href="#/login" class="auth-link">Bereits registriert? Anmelden</a>
            </div>
          </form>
        </div>
      </div>
    `;

    // Set up event listener for register form
    const registerForm = document.getElementById('register-form');
    if (registerForm) {
      registerForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this._handleRegisterSubmit(e);
      });
    }

    this._updateNavigation("root");
  }

  /**
   * Handle registration form submission
   */
  async _handleRegisterSubmit(e) {
    const firstName = document.getElementById('firstName').value;
    const lastName = document.getElementById('lastName').value;
    const gender = document.getElementById('gender').value;
    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    const confirmPassword = document.getElementById('confirmPassword').value;
    const errorMessage = document.getElementById('register-error');
    const successMessage = document.getElementById('register-success');
    const submitButton = e.target.querySelector('button[type="submit"]');

    // Validate passwords match
    if (password !== confirmPassword) {
      errorMessage.textContent = 'Passwörter stimmen nicht überein.';
      errorMessage.style.display = 'block';
      return;
    }

    try {
      errorMessage.style.display = 'none';
      successMessage.style.display = 'none';
      submitButton.disabled = true;
      submitButton.textContent = 'Konto wird erstellt...';

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName,
          lastName,
          gender,
          email,
          password,
          confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Show success message
      successMessage.textContent = data.message || 'Registrierung erfolgreich! Bitte überprüfen Sie Ihre E-Mail zur Verifizierung.';
      successMessage.style.display = 'block';

      // Hide form and show link to login
      e.target.innerHTML = `
        <div class="auth-links" style="text-align: center; margin-top: 20px;">
          <a href="#/login" class="btn-primary">Zurück zum Login</a>
        </div>
      `;

    } catch (error) {
      errorMessage.textContent = error.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      errorMessage.style.display = 'block';
      submitButton.disabled = false;
      submitButton.textContent = 'Konto erstellen';
    }
  }

  /**
   * Handle reset password route - Full page reset password form
   */
  async _handleResetPasswordRoute() {
    // Hide navigation for auth pages
    const appNav = document.getElementById("app-nav") || document.getElementById("navBar") || document.getElementById("appNavBar");
    if (appNav) {
      appNav.style.display = 'none';
    }

    const contentContainer = document.getElementById("content-container");

    contentContainer.innerHTML = `
      <div class="auth-page">
        <div class="auth-container">
          <div class="auth-header">
            <img src="/images/logo.png" alt="CaninCloud Logo" class="auth-logo">
            <h1>CaninCloud</h1>
            <p>Passwort zurücksetzen</p>
          </div>

          <div id="reset-error" class="error-message" style="display: none;"></div>
          <div id="reset-success" class="success-message" style="display: none;"></div>

          <form id="reset-form" class="auth-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required autofocus>
              <div class="form-hint">Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts</div>
            </div>

            <button type="submit" class="btn-primary btn-full-width">Zurücksetzungslink senden</button>

            <div class="auth-links">
              <a href="#/login" class="auth-link">Zurück zum Login</a>
            </div>
          </form>
        </div>
      </div>
    `;

    // Set up event listener for reset form
    const resetForm = document.getElementById('reset-form');
    if (resetForm) {
      resetForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this._handleResetPasswordSubmit(e);
      });
    }

    this._updateNavigation("root");
  }

  /**
   * Handle reset password form submission
   */
  async _handleResetPasswordSubmit(e) {
    const email = document.getElementById('email').value;
    const errorMessage = document.getElementById('reset-error');
    const successMessage = document.getElementById('reset-success');
    const submitButton = e.target.querySelector('button[type="submit"]');

    try {
      errorMessage.style.display = 'none';
      successMessage.style.display = 'none';
      submitButton.disabled = true;
      submitButton.textContent = 'Wird gesendet...';

      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset link');
      }

      // Show success message
      successMessage.textContent = data.message || 'Wenn Ihre E-Mail-Adresse existiert, haben Sie eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts erhalten.';
      successMessage.style.display = 'block';

      // Hide form and show link to login
      e.target.innerHTML = `
        <div class="auth-links" style="text-align: center; margin-top: 20px;">
          <a href="#/login" class="btn-primary">Zurück zum Login</a>
        </div>
      `;

    } catch (error) {
      errorMessage.textContent = error.message || 'Senden des Links fehlgeschlagen. Bitte versuchen Sie es erneut.';
      errorMessage.style.display = 'block';
      submitButton.disabled = false;
      submitButton.textContent = 'Zurücksetzungslink senden';
    }
  }
}

const coreRouter = new CoreRouter();
export default coreRouter;